import { useContext, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  setAccessTokenGetter,
  setSessionExpiredHandler,
} from "./setupAuthInterceptor";
import NotificationContext from "../context/NotificationContext";

/**
 * Registers the axios Bearer token getter during render (before child effects),
 * so PermissionProvider's first permissions call is authenticated.
 *
 * Also wires session-expiry handling: when Auth0 refresh fails or a protected
 * API returns 401, show a confirm dialog and log the user out cleanly.
 */
export default function AuthTokenBridge({ children }) {
  const { getAccessTokenSilently, logout, isAuthenticated } = useAuth0();
  const notificationCtx = useContext(NotificationContext);

  // Sync during render: parent body runs before children render/effects.
  setAccessTokenGetter(getAccessTokenSilently);

  useEffect(() => {
    return () => setAccessTokenGetter(null);
  }, []);

  useEffect(() => {
    return setSessionExpiredHandler(() => {
      // Ignore stray 401s while Auth0 already considers the user logged out.
      if (!isAuthenticated) return;

      const endSession = () => {
        logout({
          logoutParams: { returnTo: window.location.origin },
        });
      };

      notificationCtx.show({
        message: (
          <>
            <div style={{ fontWeight: "600", marginBottom: "8px" }}>
              <strong>Session expired</strong>
            </div>
            <div
              style={{
                lineHeight: "1.5",
                fontSize: "14px",
                textAlign: "left",
                fontWeight: 400,
              }}
            >
              Your sign-in session has ended. Please sign in again to continue.
            </div>
          </>
        ),
        stat: "error",
        onConfirm: endSession,
        // Cancel / Escape / backdrop must not leave a half-authenticated UI.
        onClose: endSession,
      });
    });
  }, [isAuthenticated, logout, notificationCtx]);

  return children;
}
